import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import {
    type AssistKind,
    type AssistOutcome,
    type GuessKey,
    type IAssistShown,
    type ISearchDialogLearn,
    MAX_ASSIST_TEXT,
    type NavMethod,
} from "./searchDialog.learn";

/**
 * One assist's offer as on screen now, or `null` when nothing is offered.
 * Two offers with the same `key` are the same offer; a new key ends the last.
 * `offered` is both sent and used here to judge how it ended.
 */
export interface IAssistOffer extends Omit<IAssistShown, "kind"> {
    key: string;
}

/** The two learn callbacks an assist needs. */
export type IAssistLearnSink = Pick<ISearchDialogLearn, "learnAssistShown" | "learnAssistEnded">;

/** How a row was taken, carried on the end event. */
export interface IAssistTake {
    rank?: number;
    navMethod?: NavMethod | GuessKey;
}

interface ILiveOffer {
    assistId: string;
    offer: IAssistOffer;
    shownAt: number;
    highlights: number;
}

export interface IAssistTracker {
    /** The user took the offer on screen. */
    accept: (take?: IAssistTake) => void;
    /** Escape, or the caret or a click moved off it. */
    dismiss: () => void;
    /** The box is about to be cleared by the page (a question sent), not by the user. */
    expire: () => void;
    /** A keyboard step through a list's rows: counted, sent on the end. */
    highlight: () => void;
    /** The offer last accepted, for the search it led to; read once. */
    takeAccepted: () => string | null;
}

/** Case and spacing only: "Jackson  Hole" typed is "jackson hole" offered. */
const norm = (text: string) => text.trim().replace(/\s+/g, " ").toLowerCase();

/** Edit distance, capped: past a few edits the number says nothing more. */
export function cappedEditDistance(a: string, b: string, cap = 20): number {
    const x = norm(a).slice(0, MAX_ASSIST_TEXT);
    const y = norm(b).slice(0, MAX_ASSIST_TEXT);
    if (Math.abs(x.length - y.length) >= cap) {
        return cap;
    }
    if (y.startsWith(x) || x.startsWith(y)) {
        return Math.min(Math.abs(x.length - y.length), cap);
    }
    let prev = Array.from({ length: y.length + 1 }, (_, j) => j);
    for (let i = 1; i <= x.length; i++) {
        const row = [i];
        for (let j = 1; j <= y.length; j++) {
            row[j] = Math.min(
                prev[j] + 1,
                row[j - 1] + 1,
                prev[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1),
            );
        }
        prev = row;
    }
    return Math.min(prev[y.length], cap);
}

/**
 * How an offer ended when the user did nothing to it directly, read off the
 * text typed now (see {@link AssistOutcome}).
 */
export function resolveImplicitOutcome(
    offer: Pick<IAssistOffer, "typed" | "offered">,
    typedNow: string,
): AssistOutcome {
    const now = norm(typedNow);
    const typed = norm(offer.typed);
    const targets = offer.offered.map(norm).filter((t) => t.length > 0);
    if (now === typed) {
        return "expired";
    }
    if (now.length === 0 || (typed.startsWith(now) && now.length < typed.length)) {
        return "backed_off";
    }
    if (targets.includes(now)) {
        return "typed_through";
    }
    if (targets.some((t) => t.startsWith(now))) {
        return "refined";
    }
    return "overridden";
}

/**
 * Turns one assist's on-screen offer into its shown → ended pair of events.
 *
 * Call each render with the offer visible now (or `null`). A new offer (new
 * `key`) ends the previous one with the outcome read off the text typed;
 * `accept`/`dismiss`/`expire` end it explicitly, and whatever is live at
 * unmount ends as `expired`. Exactly one end per show.
 *
 * Both events take `ids` as they are when sent, so an end can carry a newer
 * `searchId` than its show (a search ran while it was up). Join the pair on
 * `assistId`, never on `searchId`.
 */
export function useAssistLearn(
    learn: IAssistLearnSink,
    kind: AssistKind,
    offer: IAssistOffer | null,
    typedNow: string,
    ids: { searchId: string | null; traceId: string | null },
): IAssistTracker {
    const liveRef = useRef<ILiveOffer | null>(null);
    const acceptedRef = useRef<string | null>(null);
    // The latest of each, read when an offer ends rather than when it was shown.
    // Synced in a layout effect, which runs before the offer effect below and
    // before any handler the next paint makes reachable.
    const typedRef = useRef(typedNow);
    const idsRef = useRef(ids);
    const learnRef = useRef(learn);
    const offerRef = useRef(offer);
    useLayoutEffect(() => {
        typedRef.current = typedNow;
        idsRef.current = ids;
        learnRef.current = learn;
        offerRef.current = offer;
    });

    const end = useCallback(
        (outcome: AssistOutcome, take: IAssistTake = {}) => {
            const live = liveRef.current;
            if (live === null) {
                return;
            }
            liveRef.current = null;
            if (outcome === "accepted") {
                acceptedRef.current = live.assistId;
            }
            learnRef.current.learnAssistEnded(idsRef.current, live.assistId, {
                kind,
                outcome,
                msShown: Date.now() - live.shownAt,
                typedAtEnd: typedRef.current,
                ...(take.rank !== undefined ? { rank: take.rank } : {}),
                ...(take.navMethod !== undefined ? { navMethod: take.navMethod } : {}),
                ...(kind === "query_suggestions" ? { highlights: live.highlights } : {}),
            });
        },
        [kind],
    );

    const offerKey = offer?.key ?? null;
    useEffect(() => {
        const live = liveRef.current;
        if (live !== null && live.offer.key === offerKey) {
            return;
        }
        if (live !== null) {
            end(resolveImplicitOutcome(live.offer, typedRef.current));
        }
        const next = offerRef.current;
        if (next === null) {
            return;
        }
        const { key: _key, ...shown } = next;
        const assistId = learnRef.current.learnAssistShown(idsRef.current, { kind, ...shown });
        liveRef.current = { assistId, offer: next, shownAt: Date.now(), highlights: 0 };
    }, [offerKey, end, kind]);

    // Whatever is still on screen when the surface goes away just went away.
    useEffect(() => () => end("expired"), [end]);

    return {
        accept: useCallback((take?: IAssistTake) => end("accepted", take), [end]),
        dismiss: useCallback(() => end("dismissed"), [end]),
        expire: useCallback(() => end("expired"), [end]),
        highlight: useCallback(() => {
            if (liveRef.current !== null) {
                liveRef.current.highlights += 1;
            }
        }, []),
        takeAccepted: useCallback(() => {
            const id = acceptedRef.current;
            acceptedRef.current = null;
            return id;
        }, []),
    };
}

/** The shown-event fields describing a single offered text against what was typed. */
export function describeOffered(typed: string, offered: string) {
    return {
        offeredLength: offered.length,
        editDistance: cappedEditDistance(typed, offered),
        extendsTyped: norm(offered).startsWith(norm(typed)),
    };
}
